import { FC } from "react";

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Box, Stack } from "@mui/material";

import { useTranslate } from "@/common/hooks/useTranslate";
import { MenuCell } from "@/components/MenuCell/MenuCell";
import { DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardTooltip } from "@/dashboard-design-system/DashboardTooltip";
import { DashboardMenuItem } from "@/dashboard-design-system/MenuItem";
import { DashboardTypography } from "@/dashboard-design-system/Typography";
import { Hide, Move, Reveal } from "@/icons/outlined";

interface CommunicationPreferenceOptionRowProps {
  id: string;
  name: string;
  subscriberCount: number;
  isVisible: boolean;
  isSignUpForm?: boolean;
  onRename?: () => void;
  onDelete?: () => void;
  deleteDisabledTooltip?: string;
  onToggleVisibility: () => void;
}

export const CommunicationPreferenceOptionRow: FC<CommunicationPreferenceOptionRowProps> = ({
  id,
  name,
  subscriberCount,
  isVisible,
  isSignUpForm = false,
  onRename,
  onDelete,
  deleteDisabledTooltip,
  onToggleVisibility,
}) => {
  const { t } = useTranslate();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: !isVisible,
  });

  return (
    <Box
      ref={setNodeRef}
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        gap: 1,
        pl: isVisible ? 0.5 : 2,
        pr: 2,
        py: 1.5,
        border: `1px solid ${theme.palette.border.neutral.quiet}`,
        borderRadius: 1,
        backgroundColor: theme.palette.surface.neutral.white,
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        cursor: isDragging ? "grabbing" : "default",
      })}
      data-test="communication-preference-option"
    >
      {isVisible && (
        <DashboardIconButton
          variant="text"
          size="small"
          vibe="neutral"
          sx={{ cursor: "grab", touchAction: "none" }}
          {...attributes}
          {...listeners}
        >
          <Box sx={{ display: "flex", transform: "rotate(90deg)" }}>
            <Move />
          </Box>
        </DashboardIconButton>
      )}
      <Stack flex={1}>
        <DashboardTypography variant="subtitle2" vibe={isVisible ? "text-neutral-intense" : "text-neutral-supershy"}>
          {name}
        </DashboardTypography>
        <DashboardTypography variant="body2" vibe={isVisible ? "text-neutral-quiet" : "text-neutral-supershy"}>
          {[
            t("dashboard", "crm.lists.contactCount", { count: subscriberCount }),
            !isVisible &&
              t(
                "dashboard",
                isSignUpForm ? "communicationPreferences.hiddenFromForm" : "communicationPreferences.hidden"
              ),
          ]
            .filter(Boolean)
            .join(" • ")}
        </DashboardTypography>
      </Stack>
      <Stack direction="row" spacing={0.5}>
        <DashboardIconButton
          variant="text"
          size="small"
          vibe="neutral"
          onClick={onToggleVisibility}
          data-test="communication-preference-option-visibility"
        >
          {isVisible ? <Reveal /> : <Hide />}
        </DashboardIconButton>
        {onRename && onDelete && (
          <MenuCell horizontal="right" buttonVariant="text">
            <DashboardMenuItem onClick={onRename} data-test="communication-preference-option-rename">
              {t("dashboard", "crm.lists.rename")}
            </DashboardMenuItem>
            <DashboardTooltip title={deleteDisabledTooltip ?? ""} placement="left">
              <div>
                <DashboardMenuItem
                  color="error"
                  onClick={onDelete}
                  disabled={Boolean(deleteDisabledTooltip)}
                  data-test="communication-preference-option-delete"
                >
                  {t("dashboard", "crm.lists.delete")}
                </DashboardMenuItem>
              </div>
            </DashboardTooltip>
          </MenuCell>
        )}
      </Stack>
    </Box>
  );
};
